import {useCallback, useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {PollOverlay} from '~/common/fabric/CreateDesign/components/Polls/PollOverlay';
import {CREATE_EDIT_POLL} from '~/common/fabric/reducers/widgets';
import {
  selectHasPoll,
  selectIsMealOptionsEventType,
} from '~/common/fabric/reducers/widgets/selectors';
import {PollModule} from '~/ViewInvitation/routes/Landing/PollModule/PollModule';
import {MealOptionsIcon} from '~library/svg/MealOptionsIcon';
import {PollLineIcon} from '~library/svg/PollLineIcon';
import {OptionWrapper} from './OptionWrapper';

export const Polls = () => {
  const store = useStore();
  const hasPoll = useSelector(selectHasPoll);
  const isMealOptions = useSelector(selectIsMealOptionsEventType);
  const eventWidgets = useSelector(({widgets}) => widgets);
  const [pollSwitchOn, setPollSwitchOn] = useState(hasPoll);
  const [showOverlay, setShowOverlay] = useState(false);
  const [pollsListenForChanges, setPollsListenForChanges] = useState(false);
  const [pollsValidationErrors, setPollsValidationErrors] = useState('');

  const panelHeading = isMealOptions ? 'Get meal options' : 'Poll your guests';
  const qaIdPrefix = isMealOptions ? 'get-meal-options' : 'create-a-poll';

  // Default values if no poll has been added
  const panelDescription = isMealOptions
    ? "Get your guests' meal preferences."
    : 'Looking for guest input? Add a poll to your Invitation to get their take on meals, activities, and more.';

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const openOverlay = useCallback(() => {
    setShowOverlay(true);
  }, [setShowOverlay]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const closeOverlay = useCallback(() => {
    setShowOverlay(false);
  }, [setShowOverlay]);

  const handlePollsPanelClose = (poll) => {
    if (!poll) {
      setPollSwitchOn(false);
    }
    setPollsListenForChanges(false);
    closeOverlay();
  };
  const pollsErrorCallback = (validationErrors) => {
    setPollsValidationErrors(validationErrors);
  };
  const pollOverlayCallback = () => {
    setPollSwitchOn(true);
  };
  const handleEditPoll = () => {
    openOverlay();
  };
  const clearPollData = () => {
    store.dispatch({
      type: CREATE_EDIT_POLL,
      payload: {polls: []},
    });
  };

  const resetPollState = () => {
    clearPollData();
    closeOverlay();
    setPollsListenForChanges(true);
  };

  useEffect(() => {
    setPollsListenForChanges(true);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (pollsListenForChanges) {
      if (!hasPoll) {
        openOverlay();
      } else {
        resetPollState();
      }
    } else if (!hasPoll) {
      setPollsListenForChanges(true);
    } else if (!pollSwitchOn) {
      resetPollState();
    }
  }, [pollSwitchOn]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!hasPoll) {
      resetPollState();
    }

    if (hasPoll) {
      handlePollsPanelClose(!!eventWidgets?.polls[0]);
    }
  }, [hasPoll, pollsValidationErrors]);

  return (
    <>
      <OptionWrapper
        icon={isMealOptions ? <MealOptionsIcon /> : <PollLineIcon />}
        title={panelHeading}
        subtitle={panelDescription}
        onEdit={handleEditPoll}
        isActive={hasPoll}
        onRemove={resetPollState}
        qaIdPrefix={qaIdPrefix}
      >
        <PollModule readonly />
      </OptionWrapper>
      <PollOverlay
        isOpen={showOverlay}
        onClose={handlePollsPanelClose}
        overlayCallback={pollOverlayCallback}
        errorCallback={pollsErrorCallback}
        title={panelHeading}
        isMealOptions={isMealOptions}
        hasPoll={hasPoll}
      />
    </>
  );
};
